"use client";

/**
 * P11-10 · Reading and writing the consent choice.
 *
 * Client-safe and dependency-free, like `events.ts`, because three unrelated
 * things need it: the banner (to know whether to show), the emitter (to know
 * whether to capture), and the third-party tags (to replay Consent Mode on
 * every page load, not just at the moment of choice).
 *
 * A cookie rather than localStorage on purpose — the value has to be readable
 * by the server on the ingest request, and localStorage isn't.
 */
import {
  readClientCookie,
  writeClientCookie,
} from "~/lib/cookies/client-cookie";
import {
  bucketFor,
  CONSENT_BUCKETS,
  CONSENT_MAX_AGE_SECONDS,
  parseConsentBucket,
  VX_CONSENT_COOKIE,
  type ConsentBucket,
} from "./events";

export interface ConsentGrants {
  analytics: boolean;
  marketing: boolean;
}

/** Everything off — what a first-time visitor gets until they choose. */
export const NO_CONSENT: ConsentGrants = { analytics: false, marketing: false };

/**
 * The stored choice, or `null` when the visitor has never answered.
 *
 * The distinction is the whole reason this isn't just `parseConsentBucket`:
 * that collapses "declined everything" and "never asked" into `none`, and the
 * banner must show for the second but not the first.
 */
export function storedBucket(): ConsentBucket | null {
  const raw = readClientCookie(VX_CONSENT_COOKIE);
  return (CONSENT_BUCKETS as readonly string[]).includes(raw ?? "")
    ? (raw as ConsentBucket)
    : null;
}

export function hasStoredConsent(): boolean {
  return storedBucket() !== null;
}

/** The effective grants — a never-asked visitor gets nothing. */
export function currentGrants(): ConsentGrants {
  return grantsOf(parseConsentBucket(storedBucket()));
}

export function grantsOf(bucket: ConsentBucket): ConsentGrants {
  return {
    analytics: bucket === "analytics" || bucket === "all",
    marketing: bucket === "marketing" || bucket === "all",
  };
}

/** Fires whenever the choice changes, so tags can re-apply without a reload. */
const listeners = new Set<(grants: ConsentGrants) => void>();

export function onConsentChange(cb: (grants: ConsentGrants) => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/**
 * Persist a choice and notify listeners. Returns the stored token.
 *
 * P17A-06 · Attributes now come from `writeClientCookie` rather than being
 * spelled out here. `Secure` was conditional on `location.protocol === "https:"`
 * — because a browser silently refuses a Secure cookie over http, and dev and
 * Playwright are both plain http, so the banner would have reappeared on every
 * navigation and every spec would have been stuck behind it. The helper keys on
 * `isSecureContext` instead, which is the same predicate the browser itself
 * uses to accept the cookie: `https:` OR a localhost origin. So dev and the e2e
 * harness now get `Secure` (and keep working), and a plain-HTTP preview host
 * still does not.
 */
export function writeConsent(grants: ConsentGrants): ConsentBucket {
  const bucket = bucketFor(grants);
  writeClientCookie(VX_CONSENT_COOKIE, bucket, {
    maxAge: CONSENT_MAX_AGE_SECONDS,
  });
  for (const cb of listeners) cb(grants);
  return bucket;
}
